import { AxiosInstance } from "axios";
import { Product } from "../../../contexts/cartContext/cartType";
import { floatToCents } from "../../../functions/price_functions";

const TOTVS_VAREJO_TEMPLATE_ID = 105;

export type AvailableWalletData = {
  availableBalance: number;
  balance?: number;
  pendingBalance?: number;
  [key: string]: unknown;
};

export type AvailableWalletResponse = {
  success: boolean;
  data?: AvailableWalletData | null;
  message?: string;
  errorMsg?: string;
};

export type RedeemCashbackProductPayload = {
  id?: number;
  productId?: number;
  productSku?: string;
  productName?: string;
  quantity: number;
  price: number;
  unitPrice?: number;
  discount?: number;
};

export type RedeemCashbackPayload = {
  customerId: number;
  redeemAmount: number;
  hasCoupon?: boolean;
  deliveryTax?: number;
  orderValue?: number;
  plugintypeId?: number;
  products: RedeemCashbackProductPayload[];
};

export type RedeemCalculateResponse = {
  success: boolean;
  data?: {
    products?: Array<{
      id?: number;
      productId?: number;
      productSku?: string;
      code?: string;
      quantity?: number;
      price?: number;
      unitPrice?: number;
      discount?: number;
      cashbackDiscount?: number;
    }>;
    appliedAmount?: number;
    requestedAmount?: number;
    availableBalance?: number;
    [key: string]: unknown;
  } | null;
  message?: string;
  errorMsg?: string;
};

export type ApplyCashbackResult = {
  success: boolean;
  discountedProducts: Product[];
  appliedAmount: number;
  errorMsg?: string;
};

export type CashbackFeatureFlags = {
  enabled: boolean;
  totvsAble: boolean;
  /** Plugin Cashback com content.able ativo */
  cashbackAble: boolean;
  /** Template Cashback encontrado na loja */
  cashbackTemplateId?: number | null;
};

type CashbackTemplateItem = {
  id?: number;
  status?: boolean;
  pluginconfig?: {
    id?: number;
    plugintemplateId?: number;
    content?: {
      able?: unknown;
    };
  } | null;
};

function isConfigFlagOn(value: unknown): boolean {
  return value === 1 || value === "1" || value === true;
}

function isCashbackTemplateActive(template: CashbackTemplateItem): boolean {
  // Contrato GET /api/plugintemplate/cashback:
  // content.able: "1" = habilitado | "0" = desabilitado
  const able = template?.pluginconfig?.content?.able;
  return able === "1" || isConfigFlagOn(able);
}

let cachedFeaturePromise: Promise<CashbackFeatureFlags> | null = null;
let cachedFeatureKey: string | null = null;

/**
 * Define se o cashback da loja pode ser usado.
 * - Se TOTVS Varejo (105) estiver able: NÃO chama rotas de cashback
 * - Só habilita se GET /api/plugintemplate/cashback tiver pluginconfig.content.able = "1"
 */
export async function resolveCashbackFeatureEnabled(
  api: AxiosInstance,
  token: string,
): Promise<CashbackFeatureFlags> {
  const cacheKey = token;
  if (cachedFeaturePromise && cachedFeatureKey === cacheKey) {
    return cachedFeaturePromise;
  }

  cachedFeatureKey = cacheKey;
  cachedFeaturePromise = (async (): Promise<CashbackFeatureFlags> => {
    const defaults: CashbackFeatureFlags = {
      enabled: false,
      totvsAble: false,
      cashbackAble: false,
      cashbackTemplateId: null,
    };

    try {
      const totvsResponse = await api.post(
        `/api/pluginconfig/filter`,
        { plugintemplateId: TOTVS_VAREJO_TEMPLATE_ID },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const totvsConfig = totvsResponse?.data?.data?.[0];
      // Mesma regra do verifyTotvs do checkout: able === "1" bloqueia cashback
      const totvsAble =
        totvsConfig?.content?.able === "1" ||
        isConfigFlagOn(totvsConfig?.content?.able);
      if (totvsAble) {
        return {
          ...defaults,
          totvsAble: true,
          enabled: false,
        };
      }

      const cashbackResponse = await api.get(`/api/plugintemplate/cashback`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const body = cashbackResponse?.data;
      const templates: CashbackTemplateItem[] = Array.isArray(body?.data)
        ? body.data
        : [];

      // Sem template / lista vazia => feature off
      if (!templates.length) {
        return defaults;
      }

      const activeTemplate = templates.find(isCashbackTemplateActive);

      if (activeTemplate) {
        return {
          totvsAble: false,
          cashbackAble: true,
          cashbackTemplateId:
            activeTemplate.pluginconfig?.plugintemplateId ??
            activeTemplate.id ??
            null,
          enabled: true,
        };
      }

      // Template existe na loja, mas able != "1"
      return {
        totvsAble: false,
        cashbackAble: false,
        cashbackTemplateId:
          templates[0]?.pluginconfig?.plugintemplateId ??
          templates[0]?.id ??
          null,
        enabled: false,
      };
    } catch (error) {
      console.error("Erro ao validar feature de cashback:", error);
      cachedFeaturePromise = null;
      cachedFeatureKey = null;
      return defaults;
    }
  })();

  return cachedFeaturePromise;
}

/** Limpa o cache da feature (útil se a config da loja mudar na sessão). */
export function clearCashbackFeatureCache() {
  cachedFeaturePromise = null;
  cachedFeatureKey = null;
}

export type CalculateCashbackItemPayload = {
  id?: number;
  productId?: number;
  variationId?: number;
  sku?: string;
  productSku?: string;
  code?: string;
  quantity: number;
  price: number;
  unitPrice?: number;
  discount?: number;
  promotionalPrice?: number;
};

export type CalculateCashbackPayload = {
  customerId: number;
  customerDocument?: string;
  hasCoupon?: boolean;
  orderValue?: number;
  deliveryTax?: number;
  items: CalculateCashbackItemPayload[];
};

export type CalculateCashbackResponse = {
  success: boolean;
  data?: {
    amount?: number;
    orderValue?: number;
    shippingCashback?: number;
    items?: Array<{
      productId?: number;
      amount?: number;
      rate?: number;
      subtotal?: number;
      source?: string;
    }>;
  } | null;
  message?: string;
  errorMsg?: string;
};

/**
 * Busca saldo disponível de cashback do cliente.
 * GET /api/cashback/wallet/:customerId/available
 */
export async function getAvailableCashbackWallet(
  customerId: number,
  api: AxiosInstance,
  token: string,
  plugintypeId?: number,
): Promise<AvailableWalletResponse> {
  try {
    if (!customerId) {
      return {
        success: false,
        data: null,
        errorMsg: "Cliente inválido para consulta de cashback",
      };
    }

    const query = plugintypeId ? `?plugintypeId=${plugintypeId}` : "";
    const response = await api.get(
      `/api/cashback/wallet/${customerId}/available${query}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (response?.data?.success === false) {
      return {
        success: false,
        data: response.data?.data || null,
        message: response.data?.message,
        errorMsg:
          response.data?.message || "Cashback indisponível para este cliente",
      };
    }

    const raw = response?.data?.data ?? response?.data ?? {};
    const availableBalance = Number(raw.availableBalance ?? raw.balance ?? 0);

    return {
      success: true,
      data: {
        ...raw,
        availableBalance: Number.isFinite(availableBalance)
          ? availableBalance
          : 0,
        balance: Number(raw.balance ?? availableBalance ?? 0),
        pendingBalance: Number(raw.pendingBalance ?? 0),
      },
      message: response?.data?.message,
    };
  } catch (error: any) {
    const message =
      error?.response?.data?.message ||
      error?.message ||
      "Erro ao buscar saldo de cashback";

    return {
      success: false,
      data: null,
      errorMsg: message,
    };
  }
}

/**
 * Monta payload de produtos para o cálculo de resgate (preços em centavos).
 */
export function mapProductsToRedeemPayload(
  products: Product[],
): RedeemCashbackProductPayload[] {
  return products.map((product) => {
    const unitPriceCents = floatToCents(product.unitPrice);
    const effectivePrice =
      product.promotionalPrice > 0
        ? product.promotionalPrice
        : product.unitPrice;
    const priceCents = floatToCents(effectivePrice);
    const existingDiscountCents = Math.max(0, unitPriceCents - priceCents);

    return {
      id: Number(product.id) || undefined,
      productId: Number(product.id) || undefined,
      productSku: product.sku,
      productName: product.name,
      quantity: Number(product.quantity) || 0,
      price: priceCents,
      unitPrice: unitPriceCents,
      discount: existingDiscountCents,
    };
  });
}

/**
 * Monta items para POST /cashback/calculate (acúmulo).
 * Preços/descontos em centavos, no mesmo contrato do sendOrder.
 */
export function mapProductsToCalculateItems(
  products: Product[],
): CalculateCashbackItemPayload[] {
  return products.map((product) => {
    const quantity = Number(product.quantity) || 0;
    const unitPriceCents = floatToCents(product.unitPrice);
    const effectivePrice =
      product.promotionalPrice > 0
        ? product.promotionalPrice
        : product.unitPrice;
    const effectiveCents = floatToCents(effectivePrice);
    const lineDiscountCents = Math.max(
      0,
      (unitPriceCents - effectiveCents) * quantity,
    );

    const variationId = Number(
      product.variation?.id || product.variation?.variationId || 0,
    );

    return {
      id: Number(product.id) || undefined,
      productId: Number(product.id) || undefined,
      variationId: variationId || undefined,
      sku: product.sku,
      productSku: product.sku,
      code: product.code,
      quantity,
      price: unitPriceCents,
      unitPrice: unitPriceCents,
      discount: lineDiscountCents,
      promotionalPrice: effectiveCents,
    };
  });
}

/**
 * Calcula o cashback que o pedido pode gerar (acúmulo).
 * POST /api/cashback/calculate
 */
export async function calculateCashbackEarn(
  payload: CalculateCashbackPayload,
  api: AxiosInstance,
  token: string,
): Promise<CalculateCashbackResponse> {
  try {
    const response = await api.post("/api/cashback/calculate", payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response?.data?.success === false) {
      return {
        success: false,
        data: response.data?.data || null,
        message: response.data?.message,
        errorMsg:
          response.data?.message || "Não foi possível calcular o cashback",
      };
    }

    return {
      success: true,
      data: response?.data?.data ?? response?.data,
      message: response?.data?.message,
    };
  } catch (error: any) {
    const message =
      error?.response?.data?.message ||
      error?.message ||
      "Erro ao calcular cashback do pedido";

    return {
      success: false,
      data: null,
      errorMsg: message,
    };
  }
}

/**
 * Calcula e rateia o resgate de cashback nos produtos.
 * POST /api/cashback/redeem/calculate
 */
export async function calculateCashbackRedeem(
  payload: RedeemCashbackPayload,
  api: AxiosInstance,
  token: string,
): Promise<RedeemCalculateResponse> {
  try {
    const response = await api.post("/api/cashback/redeem/calculate", payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response?.data?.success === false) {
      return {
        success: false,
        data: response.data?.data || null,
        message: response.data?.message,
        errorMsg:
          response.data?.message || "Não foi possível aplicar o cashback",
      };
    }

    return {
      success: true,
      data: response?.data?.data ?? response?.data,
      message: response?.data?.message,
    };
  } catch (error: any) {
    const message =
      error?.response?.data?.message ||
      error?.message ||
      "Erro ao calcular resgate de cashback";

    return {
      success: false,
      data: null,
      errorMsg: message,
    };
  }
}

function matchRedeemProduct(
  product: Product,
  apiProduct: NonNullable<
    NonNullable<RedeemCalculateResponse["data"]>["products"]
  >[number],
): boolean {
  const productId = Number(product.id);
  const apiId = Number(apiProduct.id || apiProduct.productId || 0);
  if (apiId && productId && apiId === productId) return true;

  if (apiProduct.productSku && product.sku) {
    return String(apiProduct.productSku) === String(product.sku);
  }

  if (apiProduct.code && product.code) {
    return String(apiProduct.code) === String(product.code);
  }

  return false;
}

/**
 * Aplica o desconto de cashback retornado pela API nos produtos do carrinho.
 * O desconto da API vem em centavos (por unidade, no mesmo padrão do motor Ninegrid).
 */
export function applyCashbackDiscountToProducts(
  products: Product[],
  apiProducts: NonNullable<
    NonNullable<RedeemCalculateResponse["data"]>["products"]
  >,
  appliedAmount: number,
): ApplyCashbackResult {
  try {
    if (!apiProducts?.length) {
      return {
        success: false,
        discountedProducts: products,
        appliedAmount: 0,
        errorMsg: "Resposta de cashback sem produtos",
      };
    }

    let totalAppliedCents = 0;

    const discountedProducts = products.map((product) => {
      const apiProduct = apiProducts.find((item) =>
        matchRedeemProduct(product, item),
      );

      if (!apiProduct) {
        return product;
      }

      const quantity = Number(product.quantity) || 1;
      const rawDiscountCents = Number(
        apiProduct.cashbackDiscount ?? apiProduct.discount ?? 0,
      );

      if (!rawDiscountCents || rawDiscountCents <= 0) {
        return product;
      }

      // Compatível com desconto por unidade ou total da linha
      const unitPriceCents = floatToCents(product.unitPrice);
      let perUnitDiscountCents = rawDiscountCents;

      if (
        rawDiscountCents > unitPriceCents &&
        quantity > 1 &&
        rawDiscountCents % quantity === 0
      ) {
        perUnitDiscountCents = Math.round(rawDiscountCents / quantity);
      } else if (rawDiscountCents > unitPriceCents) {
        perUnitDiscountCents = Math.round(rawDiscountCents / quantity);
      }

      const discountReais = perUnitDiscountCents / 100;
      const currentPromo =
        product.promotionalPrice > 0
          ? product.promotionalPrice
          : product.unitPrice;
      const nextPromo = Math.max(
        0,
        Number((currentPromo - discountReais).toFixed(2)),
      );

      totalAppliedCents += perUnitDiscountCents * quantity;

      return {
        ...product,
        oldCashbackPrice: currentPromo,
        cashbackDiscount: perUnitDiscountCents,
        cashbackPrice: nextPromo,
        promotionalPrice: nextPromo,
        withDiscout: true,
        oldDiscountPrice:
          product.oldDiscountPrice ??
          product.promotionalPrice ??
          product.unitPrice,
      };
    });

    const computedApplied =
      appliedAmount > 0
        ? Number(appliedAmount.toFixed(2))
        : Number((totalAppliedCents / 100).toFixed(2));

    if (computedApplied <= 0) {
      return {
        success: false,
        discountedProducts: products,
        appliedAmount: 0,
        errorMsg: "Nenhum valor de cashback foi aplicado",
      };
    }

    return {
      success: true,
      discountedProducts,
      appliedAmount: computedApplied,
    };
  } catch (error) {
    console.error("Erro ao aplicar cashback nos produtos:", error);
    return {
      success: false,
      discountedProducts: products,
      appliedAmount: 0,
      errorMsg: "Erro ao aplicar cashback nos produtos",
    };
  }
}

export type CashbackTransactionType = "earn" | "redeem" | "reversal";

export type CashbackTransaction = {
  id: number;
  customerCashbackId?: number;
  orderId?: number | null;
  type: CashbackTransactionType;
  amount: number;
  description?: string | null;
  created?: string;
  pluginspaceId?: number;
  [key: string]: unknown;
};

export type CashbackTransactionsResponse = {
  success: boolean;
  data: CashbackTransaction[];
  message?: string;
  errorMsg?: string;
};

/**
 * Busca extrato de cashback do cliente.
 * GET /api/cashback/transactions/:customerId
 */
export async function getCashbackTransactions(
  customerId: number,
  api: AxiosInstance,
  token: string,
): Promise<CashbackTransactionsResponse> {
  try {
    if (!customerId) {
      return {
        success: false,
        data: [],
        errorMsg: "Cliente inválido para consulta de extrato",
      };
    }

    const response = await api.get(`/api/cashback/transactions/${customerId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response?.data?.success === false) {
      return {
        success: false,
        data: [],
        message: response.data?.message,
        errorMsg:
          response.data?.message || "Não foi possível carregar o extrato",
      };
    }

    const list = Array.isArray(response?.data?.data)
      ? response.data.data
      : Array.isArray(response?.data)
        ? response.data
        : [];

    return {
      success: true,
      data: list as CashbackTransaction[],
      message: response?.data?.message,
    };
  } catch (error: any) {
    const message =
      error?.response?.data?.message ||
      error?.message ||
      "Erro ao buscar extrato de cashback";

    return {
      success: false,
      data: [],
      errorMsg: message,
    };
  }
}

/**
 * Remove apenas o desconto de cashback, preservando cupom/promo.
 */
export function removeCashbackFromProducts(products: Product[]): Product[] {
  try {
    return products.map((product) => {
      if (
        product.oldCashbackPrice === undefined &&
        !product.cashbackDiscount &&
        product.cashbackPrice === undefined
      ) {
        return product;
      }

      const hasCouponOnProduct = product.cupomDiscount !== undefined;
      const restoredPrice =
        product.oldCashbackPrice ??
        product.cupomDiscount ??
        product.oldDiscountPrice ??
        product.unitPrice;

      return {
        ...product,
        promotionalPrice: restoredPrice,
        cashbackDiscount: undefined,
        oldCashbackPrice: undefined,
        cashbackPrice: undefined,
        // Mantém marcadores de cupom se houver
        withDiscout: hasCouponOnProduct ? true : false,
        oldDiscountPrice: hasCouponOnProduct
          ? product.oldDiscountPrice
          : undefined,
        cupomDiscount: hasCouponOnProduct ? product.cupomDiscount : undefined,
      };
    });
  } catch (error) {
    console.error("Erro ao remover cashback dos produtos:", error);
    return products;
  }
}
